import React, { useState, useCallback } from 'react';
import GoogleSignin from './GoogleSignin';
import FacebookLogin from './FacebookLogin';
import api from 'config/axiosConfig';
import { useUser } from 'util/UserContext';
import Modal from 'ui/Modal';
import FormActions from 'ui/FormActions';
import LocalSignin, { LOCAL_SIGNIN_FORM_ID } from './LocalSignin';
import PasswordRecovery, { PASSWORD_RECOVERY_FORM_ID, type PasswordRecoveryState } from './PasswordRecovery';
import EmailVerificationModal, { EMAIL_VERIFICATION_FORM_ID } from './EmailVerificationModal';
import { Info } from 'lucide-react';
import OTPLogin, { OTP_LOGIN_SEND_FORM_ID, OTP_LOGIN_VERIFY_FORM_ID } from './OTPLogin';
import AuthService from 'services/authService';
import { hasExistingAccountCookie, setExistingAccountCookie } from 'util/existingUserCookie';
import { LAYER_TOKENS } from 'ui/layers';
import { apiRoutes } from "util/apiRoutes";
import { getAuthUiStrings, resolveAuthLocale } from "i18n/authUi";
import { getApiErrorMessage } from "lib/errors/normalizeApiError";

export type AuthView = 'auth' | 'forgotPassword' | 'emailVerification' | 'otpLogin';

interface AuthModalProps {
  show: boolean;
  onClose: () => void;
  passwordReset?: boolean;
  /**
   * Forces which auth mode the modal should default to when opened.
   * - "signup": always open in sign-up/register mode (ignores existing-account cookie/localStorage)
   * - "signin": always open in sign-in mode
   * - null/undefined: decide based on existing-account heuristics
   */
  forcedAuthMode?: "signin" | "signup" | null;
}

interface SubmitFooterState {
  isSubmitting: boolean;
  isButtonDisabled: boolean;
  buttonLabel: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ show, onClose, passwordReset, forcedAuthMode }) => {
  const { refreshUser, setShowUsernameModal, setShowUserOnboardModal, setUserOnboardInitialView } = useUser();
  const authUi = getAuthUiStrings(resolveAuthLocale());
  const isQuizAuthFlow = typeof window !== "undefined" && window.location.pathname.startsWith("/quiz");

  /**
   * Decide the initial register-vs-signin state. Centralized so we can
   * reuse it on close/reset.
   */
  const getDefaultIsRegistering = (): boolean => {
    if (forcedAuthMode === "signup") {
      return true;
    }
    if (forcedAuthMode === "signin") {
      return false;
    }
    return !hasExistingAccountCookie();
  };

  const [activeView, setActiveView] = useState<AuthView>(
    passwordReset ? 'forgotPassword' : 'auth'
  );
  const [authError, setAuthError] = useState<string>('');
  const [emailForReset, setEmailForReset] = useState('');
  const [emailForVerification, setEmailForVerification] = useState('');
  const [isRegisteringToggle, setIsRegisteringToggle] = useState<boolean>(() => getDefaultIsRegistering());
  const [otpEmailSent, setOtpEmailSent] = useState(false);
  const [lsState, setLsState] = useState<{ isSubmitting: boolean; isRegistering: boolean; isButtonDisabled: boolean }>(() => ({
    isSubmitting: false,
    isRegistering: getDefaultIsRegistering(),
    isButtonDisabled: true,
  }));
  const [otpState, setOtpState] = useState<SubmitFooterState>(() => ({
    isSubmitting: false,
    isButtonDisabled: true,
    buttonLabel: authUi.sendCodeCta,
  }));
  const [emailVerificationState, setEmailVerificationState] = useState<SubmitFooterState>(() => ({
    isSubmitting: false,
    isButtonDisabled: true,
    buttonLabel: authUi.emailVerificationContinue,
  }));
  const [passwordRecoveryState, setPasswordRecoveryState] = useState<PasswordRecoveryState>(() => ({
    isSubmitting: false,
    isButtonDisabled: true,
    isSuccess: false,
  }));

  const handleStateChange = useCallback((state: { isSubmitting: boolean; isRegistering: boolean; isButtonDisabled: boolean }) => {
    setLsState(state);
  }, []);

  const handleGoogleCodeResponse = async (response: { code: string }): Promise<void> => {
    try {
      const result = await api.post(apiRoutes.auth.googleCallback, { code: response.code });
      const userStatus = result.status;
      if (result.data.accessToken) {
        AuthService.setAccessToken(result.data.accessToken);
      }
      await refreshUser();
      try {
        let accountId: number | null = null;
        let uname: string | null = null;
        const data = result.data as Record<string, unknown>;
        if (typeof data?.id === 'number') accountId = data.id;
        const userField = data?.user as Record<string, unknown> | undefined;
        if (typeof userField?.id === 'number') accountId = userField.id;
        if (typeof data?.username === 'string') uname = data.username;
        if (typeof userField?.username === 'string') uname = userField.username;
        if (accountId === null || !uname) {
          const userResp = await api.get(apiRoutes.user.root);
          const userData = userResp.data as { id: number | string; username?: string };
          const parsedId = typeof userData.id === 'string' ? parseInt(userData.id, 10) : userData.id;
          if (typeof parsedId === 'number') accountId = parsedId;
          if (!uname && typeof userData.username === 'string') uname = userData.username;
        }
        if (accountId !== null && typeof uname === 'string' && uname.length > 0) {
          setExistingAccountCookie(accountId, uname);
        }
      } catch (e) {
        // Cookie write is best-effort; never block the auth flow on it.
      }
      if (userStatus === 201) {
        onClose();
        setShowUsernameModal(true);
      } else if (userStatus === 200) {
        onClose();
      } else {
        setAuthError(authUi.authFailedFallback);
      }
    } catch (error) {
      console.error('Error handling Google authorization code:', error);
      setAuthError(getApiErrorMessage(error, authUi.authFailedFallback));
    }
  };

  const handleFacebookResponse = async (response: { status?: string; authResponse?: { accessToken: string; userID: string } }): Promise<void> => {
    if (response.status !== 'connected') {
      if (window.FB) {
        window.FB.login((loginResp: { authResponse?: { accessToken: string; userID: string } }) => {
          if (loginResp.authResponse) {
            handleFacebookResponse({ status: 'connected', authResponse: loginResp.authResponse });
          } else {
            setAuthError(authUi.socialAuthCanceled);
          }
        }, { scope: 'public_profile,email' });
      } else {
        return;
      }
    }
    if (!response.authResponse) {
      return;
    }
    const { accessToken, userID } = response.authResponse;
    try {
      const result = await api.post(apiRoutes.auth.facebookCallback, { accessToken, userID });
      const userStatus = result.status;
      if (result.data.accessToken) {
        AuthService.setAccessToken(result.data.accessToken);
      }
      await refreshUser();
      try {
        let accountId: number | null = null;
        let uname: string | null = null;
        const data = result.data as Record<string, unknown>;
        if (typeof data?.id === 'number') accountId = data.id;
        const userField = data?.user as Record<string, unknown> | undefined;
        if (typeof userField?.id === 'number') accountId = userField.id;
        if (typeof data?.username === 'string') uname = data.username;
        if (typeof userField?.username === 'string') uname = userField.username;
        if (accountId === null || !uname) {
          const userResp = await api.get(apiRoutes.user.root);
          const userData = userResp.data as { id: number | string; username?: string };
          const parsedId = typeof userData.id === 'string' ? parseInt(userData.id, 10) : userData.id;
          if (typeof parsedId === 'number') accountId = parsedId;
          if (!uname && typeof userData.username === 'string') uname = userData.username;
        }
        if (accountId !== null && typeof uname === 'string' && uname.length > 0) {
          setExistingAccountCookie(accountId, uname);
        }
      } catch (e) {
        // Cookie write is best-effort; never block the auth flow on it.
      }
      if (userStatus === 201) {
        onClose();
        setShowUsernameModal(true);
      } else if (userStatus === 200) {
        onClose();
      } else {
        setAuthError(authUi.authFailedFallback);
      }
    } catch (error: unknown) {
      console.error('Error handling Facebook response:', error);
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401 && window.FB) {
        window.FB.login((loginResp: { authResponse?: { accessToken: string; userID: string } }) => {
          if (loginResp.authResponse) {
            handleFacebookResponse({ status: 'connected', authResponse: loginResp.authResponse });
          } else {
            setAuthError(authUi.socialAuthCanceled);
          }
        }, { scope: 'public_profile,email' });
      } else {
        setAuthError(getApiErrorMessage(error, authUi.authFailedFallback));
      }
    }
  };

  const handleForgotPassword = (email: string) => {
    setEmailForReset(email);
    setActiveView('forgotPassword');
  };

  const handleUseCode = () => {
    setOtpEmailSent(false);
    setActiveView('otpLogin');
  };

  const handleRegistrationSuccess = (email: string) => {
    setEmailForVerification(email);
    setActiveView('emailVerification');
  };

  const handleBack = () => {
    if (activeView === 'forgotPassword') {
      setActiveView('auth');
      return;
    }

    if (activeView === 'otpLogin') {
      setOtpEmailSent(false);
      setActiveView('auth');
    }
  };

  const handleClose = () => {
    setActiveView('auth');
    setEmailForReset('');
    setEmailForVerification('');
    setOtpEmailSent(false);
    setOtpState({
      isSubmitting: false,
      isButtonDisabled: true,
      buttonLabel: authUi.sendCodeCta,
    });
    setEmailVerificationState({
      isSubmitting: false,
      isButtonDisabled: true,
      buttonLabel: authUi.emailVerificationContinue,
    });
    setPasswordRecoveryState({
      isSubmitting: false,
      isButtonDisabled: true,
      isSuccess: false,
    });
    setAuthError('');
    setIsRegisteringToggle(getDefaultIsRegistering());
    onClose();
  };

  const handleEmailVerificationExit = async () => {
    try {
      await refreshUser();
    } finally {
      handleClose();
      if (!isQuizAuthFlow) {
        // After signup, continue directly into onboarding at the issues step.
        setUserOnboardInitialView('issues');
        setShowUserOnboardModal(true);
      }
    }
  };

  /**
   * Resolve the form id whose `<form>` is currently mounted in the
   * modal body. The modal-footer Submit button uses `form={activeFormId}`
   * so browser-native Enter-key submit fires the right form's `onSubmit`.
   *
   * NOTE: each child component owns its own form id constant to keep
   * this routing in one place; new auth views must export their form id
   * and add it here.
   */
  const activeFormId =
    activeView === 'auth' ? LOCAL_SIGNIN_FORM_ID :
    activeView === 'otpLogin'
      ? (otpEmailSent ? OTP_LOGIN_VERIFY_FORM_ID : OTP_LOGIN_SEND_FORM_ID)
      : activeView === 'emailVerification'
      ? EMAIL_VERIFICATION_FORM_ID
      : activeView === 'forgotPassword' && !passwordRecoveryState.isSuccess
      ? PASSWORD_RECOVERY_FORM_ID
      : undefined;

  /**
   * Footer is rendered through the shared `FormActions` primitive for
   * every view so the loading visual is identical across all primary
   * buttons (centered spinner only, no text, no ellipsis -- see
   * `docs/architecture/forms-standard.md` section 4 on primary-button
   * consistency). Each view supplies its own submitLabel + disabled
   * state via `onStateChange`. The Cancel button's onClick varies per
   * view (e.g. emailVerification needs to refresh user state on exit).
   */
  let footer: React.ReactNode = null;
  if (activeView === 'auth') {
    footer = (
      <FormActions
        formId={LOCAL_SIGNIN_FORM_ID}
        submitLabel={lsState.isRegistering ? authUi.signUpCta : authUi.signInCta}
        cancelLabel={authUi.cancelCta}
        submittingLabel={authUi.submittingCta}
        submitDisabled={lsState.isButtonDisabled}
        submitting={lsState.isSubmitting}
        onCancel={handleClose}
      />
    );
  } else if (activeView === 'otpLogin') {
    footer = (
      <FormActions
        formId={otpEmailSent ? OTP_LOGIN_VERIFY_FORM_ID : OTP_LOGIN_SEND_FORM_ID}
        submitLabel={otpState.buttonLabel}
        cancelLabel={authUi.cancelCta}
        submittingLabel={authUi.submittingCta}
        submitDisabled={otpState.isButtonDisabled}
        submitting={otpState.isSubmitting}
        onCancel={handleClose}
      />
    );
  } else if (activeView === 'emailVerification') {
    footer = (
      <FormActions
        formId={EMAIL_VERIFICATION_FORM_ID}
        submitLabel={emailVerificationState.buttonLabel}
        cancelLabel={authUi.cancelCta}
        submittingLabel={authUi.submittingCta}
        submitDisabled={emailVerificationState.isButtonDisabled}
        submitting={emailVerificationState.isSubmitting}
        onCancel={handleEmailVerificationExit}
      />
    );
  } else if (activeView === 'forgotPassword') {
    if (passwordRecoveryState.isSuccess) {
      // Success state: a single terminal "Done" affordance that just
      // closes the modal. There is no live form to submit, so the
      // primary button is rendered as `type="button"` and dispatches
      // through `onSubmit` (close handler).
      footer = (
        <FormActions
          formId={PASSWORD_RECOVERY_FORM_ID}
          submitLabel={authUi.doneCta}
          submittingLabel={authUi.submittingCta}
          submitDisabled={false}
          submitting={false}
          hideCancel
          submitButtonType="button"
          onCancel={handleClose}
          onSubmit={handleClose}
        />
      );
    } else {
      footer = (
        <FormActions
          formId={PASSWORD_RECOVERY_FORM_ID}
          submitLabel={authUi.resetPasswordCta}
          cancelLabel={authUi.cancelCta}
          submittingLabel={authUi.submittingCta}
          submitDisabled={passwordRecoveryState.isButtonDisabled}
          submitting={passwordRecoveryState.isSubmitting}
          onCancel={handleClose}
        />
      );
    }
  }

  return (
    <Modal
      isOpen={show}
      onClose={activeView === 'emailVerification' ? handleEmailVerificationExit : handleClose}
      title={
        activeView === 'forgotPassword'
          ? authUi.forgotPasswordTitle
          : activeView === 'emailVerification'
          ? authUi.emailVerificationTitle
          : activeView === 'otpLogin'
          ? (otpEmailSent ? authUi.otpUseCodeTitle : authUi.otpSendCodeTitle)
          : (isRegisteringToggle ? authUi.signUpTitle : authUi.signInTitle)
      }
      showBackButton={activeView === 'forgotPassword' || activeView === 'otpLogin'}
      onBack={handleBack}
      showSkip={activeView === 'emailVerification'}
      footer={footer}
      stickyFooter
      layerBase={LAYER_TOKENS.prioritySurface}
      className="auth-modal text-center"
      formId={activeFormId}
    >
      <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
        {activeView === 'auth' && (
          <div className="flex flex-col flex-grow space-y-4">
            <p className="text-center text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {/*
                Build the agreement message via key-based ICU substitution so
                we never concatenate translated fragments at render time
                (rule 13). The message contains literal `{userAgreement}` and
                `{privacyPolicy}` markers we replace with anchor tags below.
              */}
              {authUi.agreementMessage.split(/(\{userAgreement\}|\{privacyPolicy\})/g).map((part, i) => {
                if (part === "{userAgreement}") {
                  return (
                    <a
                      key={`ua-${i}`}
                      href="https://www.societea.io/policies/user-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      {authUi.agreementUserAgreementLink}
                    </a>
                  );
                }
                if (part === "{privacyPolicy}") {
                  return (
                    <a key={`pp-${i}`} href="/policies/privacy-policy" className="external-link">
                      {authUi.agreementPrivacyPolicyLink}
                    </a>
                  );
                }
                return <React.Fragment key={`t-${i}`}>{part}</React.Fragment>;
              })}
            </p>

            {authError && (
              <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
                <Info className="mr-1 size-4" />
                <span>{authError}</span>
              </div>
            )}

            <div className="flex flex-col space-y-4 w-full mb-4 mx-auto items-center justify-center">
              <GoogleSignin
                label={authUi.googleContinueButton}
                onCodeReceived={handleGoogleCodeResponse}
                onError={() => setAuthError(authUi.authFailedFallback)}
              />
              <FacebookLogin handleFacebookResponse={handleFacebookResponse} />
            </div>

            <LocalSignin
              onClose={onClose}
              onForgotPassword={handleForgotPassword}
              onUseCode={handleUseCode}
              onRegistrationSuccess={handleRegistrationSuccess}
              initialEmail=""
              initialIsRegistering={isRegisteringToggle}
              onRegisteringChange={(v) => setIsRegisteringToggle(v)}
              onStateChange={handleStateChange}
            />
          </div>
        )}

        {activeView === 'forgotPassword' && (
          <PasswordRecovery
            email={emailForReset}
            onStateChange={setPasswordRecoveryState}
          />
        )}

        {activeView === 'emailVerification' && (
          <EmailVerificationModal
            onClose={handleEmailVerificationExit}
            onStateChange={setEmailVerificationState}
            email={emailForVerification}
          />
        )}

        {activeView === 'otpLogin' && (
          <OTPLogin
            onClose={handleClose}
            onCodeSentChange={setOtpEmailSent}
            onStateChange={setOtpState}
          />
        )}
      </div>

    </Modal>
  );
};

export default AuthModal;
